import {
    Component,
    Input,
    ViewChild,
    ElementRef,
    Output,
    EventEmitter,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { JobMessage } from './models';
import { MessageJobSeverity } from '@app/Enums/message-job-severity.enum';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { MsalBroadcastService } from '@azure/msal-angular';
import { EventMessage, EventType } from '@azure/msal-browser';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';



@Component({
    selector: 'job-liveview',
    templateUrl: './job.liveview.component.html',
    styleUrls: ['./job.liveview.component.scss'],
    imports: []
})
export class JobLiveViewComponent implements OnDestroy, OnInit {
    @Output() finished: EventEmitter<MessageJobSeverity> =
        new EventEmitter<MessageJobSeverity>();
    @ViewChild('console', { static: true }) private console: ElementRef;

    messages = new Array<JobMessage>();
    private readonly _destroying$ = new Subject<void>();
    private liveJobId = '';

    @Input()
    set jobId(newId: string) {
        console.log(`Changing from ${this.liveJobId} to ${newId}`);
        this.leaveGroup(this.liveJobId);

        this.liveJobId = newId;
        this.tryJoinSignalrGroup();
    }

    get jobId(): string {
        return this.liveJobId;
    }


    private hubConnection: HubConnection;
    public constructor(private broadcastService: MsalBroadcastService) {

        this.broadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
                takeUntil(this._destroying$)
            )
            .subscribe((result: EventMessage) => {
                console.log(result);
                this.connectSignalR(result.payload === null ? "" : result.payload['accessToken']);
            });

    }

    private connectSignalR(accessToken: string) {

        this.hubConnection = new HubConnectionBuilder()
            .withAutomaticReconnect()
            .withKeepAliveInterval(30000)
            .withUrl("/signalr/jobs", {
                accessTokenFactory: () => accessToken
            })
            .build();

        this.hubConnection.on('newMessage', (message: JobMessage) => {
            this.messages.push(message);
            this.scrollToBottom();

            switch (message.Severity) {
                case MessageJobSeverity.CopyConfigSuccess:
                case MessageJobSeverity.CopyConfigExportOnlySuccess:
                case MessageJobSeverity.CloneSuccess:
                case MessageJobSeverity.CloneExportOnlySuccess:
                case MessageJobSeverity.Success:
                case MessageJobSeverity.Failed:
                    this.finished.emit(message.Severity);
            }
        });

        this.hubConnection
            .start()
            .then(() => {
                this.tryJoinSignalrGroup();
            }, (err: string) => {
                console.error('Error while starting SignalR connection: ' + err);
            });
    }

    ngOnInit(): void {
    }

    private tryJoinSignalrGroup(): void {
        if (!this.liveJobId) {
            console.error("No LiveJobID provided. Can not join jobs group");
            return;
        }

        if (this.hubConnection === undefined) {
            console.log("SignalR is still initialising ...")
            return;
        }

        if (this.hubConnection.state == HubConnectionState.Connecting) {
            console.log("SignalR is still connecting to hub ...");
            return;
        }

        if (this.hubConnection.state == HubConnectionState.Connected) {
            this.joinGroup();
            return;
        }

        if (this.hubConnection.state == HubConnectionState.Disconnected) {
            console.error("SignalR is disconected from hub")
            return;
        }
    }

    private joinGroup(): void {
        this.hubConnection.invoke('JoinGroup', this.liveJobId).then((r) => {
            console.info(`Joined job group ${this.liveJobId}`);
            this.messages = r;

        });
    }

    private leaveGroup(groupId: string): void {
        if (this.liveJobId && this.hubConnection.state == HubConnectionState.Connected) {
            console.info(`Leaving jobs group ${this.liveJobId}`);
            this.hubConnection.invoke('LeaveGroup', groupId);
        }
    }

    getClass(sev: number): string {
        switch (sev) {
            case 0:
                return '';
            case 100:
                return 'warn';
            case 150:
                return 'partialsuccess';
            case 175:
                return 'important';
            case 200:
                return 'error';
            case 1000:
            case 2000:
            case 2001:
            case 2010:
            case 2011:
                return 'success';
            case 1500:
                return 'failed';
        }
        return '';
    }

    scrollToBottom(): void {
        try {
            this.console.nativeElement.scrollTop =
                this.console.nativeElement.scrollHeight;
        } catch (err) { }
    }


    ngOnDestroy(): void {
        if (this.liveJobId) {
            this.hubConnection
                .invoke('LeaveGroup', this.liveJobId)
                .then(() => this.hubConnection.stop());
        } else {
            this.hubConnection.stop();
        }

        this._destroying$.next(undefined);
        this._destroying$.complete();
    }
}